import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  Box,
  Divider,
  Typography,
  Checkbox,
  Button,
  makeStyles,
} from "@material-ui/core";

import { useQuery, useManualQuery, useMutation, ClientContext } from "graphql-hooks";

import Header from "./Header";
import AlertDialog from "../../components/AlertDialog.js";

import {
  GET_ALL_GROUP_PERMISSIONS_QUERY,
  UPDATE_GROUP_PERMISSIONS,
  GET_GROUP_DATA_BY_ID,
} from "../../graphQL/group_qry";
import {
  REFRESH_TOKEN_MUTATION,
  STAFF_MODULE_ACCESS_QUERY,
} from "../../graphQL/user_qry";
import { project_url } from "../../constants/Data";

const useStyles = makeStyles(theme => ({
  root: {
    marginRight: "8rem",
    textAlign: "center",
    padding: theme.spacing(2),
    paddingLeft: 0,
    paddingRight: 0,
    width: "1400px",
    paddingTop: "7rem",
  },
  container: {
    padding: theme.spacing(4),
    paddingTop: 0,
    marginTop: "1rem",
    marginBottom: "5rem",
    overflow: "hidden",
    clear: "both",
    borderRadius: "8px",
    minHeight: "870px",
    backgroundColor: "#ffffff",
  },
  buttonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#fff",
    height: "34px",
    backgroundColor: "#0D47A1",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#0D47A1",
    },
    "&:hover": {
      backgroundColor: "#1E88E5",
    },
  },
  outlinedButtonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#0D47A1",
    height: "34px",
    textTransform: "none",
  },
}));
const transformPermissions = data => {
  return data.map(mod => {
    return {
      ...mod,
      modulepage: mod.modulepage.map(page => {
        const action = mod.groupModulepageAction.find(
          a => parseInt(a.modulepageId) === parseInt(page.modulepageId),
        );

        const parsePermissions = (names, values) => {
          if (!names) return [];
          return names.split("|").map(name => ({
            permission: name.trim(),
            checked: values?.includes(`${name}:1`) || false,
          }));
        };

        return {
          ...page,
          permission: page.name,
          access: action?.access || false,
          common: parsePermissions(
            page.actionCommonName,
            action?.actionCommonPermission || "",
          ),
          additional: parsePermissions(
            page.actionAdditionalName,
            action?.actionAdditionalPermission || "",
          ),
        };
      }),
    };
  });
};
const extractCommonHeaders = data => {
  //removes repeated values automatically by using a Set
  const headerSet = new Set();
  data.forEach(mod => {
    mod.modulepage.forEach(page => {
      if (page.actionCommonName) {
        page.actionCommonName.split("|").forEach(perm => {
          headerSet.add(perm.trim());
        });
      }
    });
  });
  return Array.from(headerSet);
};

const GroupPermission = () => {
  const classes = useStyles();

  const { groupUuid } = useParams();

  const [loadContent, setLoadContent] = useState(false);
  const client = useContext(ClientContext);

  const [commonHeaders, setCommonHeaders] = useState([]);

  const navigate = useNavigate();
  const [pathname] = useState(window.location.pathname);
  const [refreshToken] = useMutation(REFRESH_TOKEN_MUTATION, {
    onError: err => {
      console.log(err);
    },
  });
  useEffect(() => {
    if (localStorage.getItem("refreshToken")) {
      onRefreshToken();
    } else window.location.href = `${project_url}/signin/?next=${pathname}`;
  }, []);

  const onRefreshToken = async () => {
    const { data: refreshData, error: refreshError } = await refreshToken({
      variables: { refreshToken: localStorage.getItem("refreshToken") },
    });
    if (refreshError) {
      if (
        refreshError.graphQLErrors[0].message ===
        "You do not have permission to perform this action"
      )
        window.location.href = `${project_url}/signin/?next=${pathname}`;
      if (refreshError.graphQLErrors[0].message === "Signature has expired")
        window.location.href = `${project_url}/signin/?next=${pathname}`;
    }
    if (refreshData && refreshData.refreshToken) {
      localStorage.setItem("token", refreshData.refreshToken.token);
      localStorage.setItem("refreshToken", refreshData.refreshToken.refreshToken);
      client.setHeader("Authorization", `JWT ${refreshData.refreshToken.token}`);
      setLoadContent(true);
    }
  };

  const tenantName = localStorage.getItem("tenantName");
  const tenantId = localStorage.getItem("tenantId");

  let tenantUserId = 0;
  if (localStorage.getItem("tenantUserId"))
    tenantUserId = localStorage.getItem("tenantUserId");

  let isTenantAdmin = false;
  if (localStorage.getItem("isTenantAdmin"))
    isTenantAdmin = JSON.parse(localStorage.getItem("isTenantAdmin"));

  let clientId = 0;
  if (localStorage.getItem("clientId")) clientId = localStorage.getItem("clientId");

  const [permissionAccess, setPermissionAccess] = useState(true);

  const [groupName, setGroupName] = useState("");
  const [modulesPermissions, setModulesPermissions] = useState([]);

  const [checkStaffModuleAccess] = useManualQuery(STAFF_MODULE_ACCESS_QUERY, {
    fetchPolicy: "network-only",
  });

  const checkAccess = async () => {
    //check if staff has access to module

    const {
      loading: accessDataLoading,
      error: accessDataError,
      data: accessData,
    } = await checkStaffModuleAccess({
      variables: {
        tenantUserId: parseInt(tenantUserId),
        clientId: parseInt(clientId),
        moduleName: "General Settings",
        modulepageName: "Manage Groups",
      },
    });

    if (!accessDataLoading && accessData && accessData.staffModuleAccess) {
      let additionalActions = accessData.staffModuleAccess.additionalActions.split("|");

      if (additionalActions.find(a => a == "Configure Group Permissions")) {
        setPermissionAccess(true);
      } else {
        setPermissionAccess(false);
      }
    }

    if (
      accessDataError &&
      accessDataError.graphQLErrors &&
      accessDataError.graphQLErrors[0].message === "Signature has expired"
    ) {
      navigate(`${project_url}/signin`);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    (() => checkAccess())();
  }, []);

  const [alertMsg, setAlertMsg] = useState([]);

  const [getGroupData] = useManualQuery(GET_GROUP_DATA_BY_ID, {
    fetchPolicy: "network-only",
  });

  const getGroup = async () => {
    if (groupUuid) {
      // Get group information
      const {
        loading: groupDataLoading,
        error: groupDataError,
        data: groupData,
      } = await getGroupData({
        variables: { groupUuid: groupUuid },
      });

      if (groupData.getGroupDataById == null) {
        navigate(`${project_url}/tenant/dashboard`);
      }

      if (!groupDataLoading && groupData && groupData.getGroupDataById) {
        setGroupName(groupData.getGroupDataById.name);
      }
      if (
        groupDataError &&
        groupDataError.graphQLErrors &&
        groupDataError.graphQLErrors[0].message === "Signature has expired"
      ) {
        navigate(`${project_url}/signin`);
      }
    }
  };

  useEffect(() => {
    (() => getGroup())();
  }, []);

  const [getGroupPermissionsData] = useManualQuery(GET_ALL_GROUP_PERMISSIONS_QUERY, {
    fetchPolicy: "network-only",
  });

  const getGroupPermissions = async () => {
    if (groupUuid) {
      // Get group permission information
      const {
        loading: groupPermissionDataLoading,
        error: groupPermissionDataError,
        data: groupPermissionData,
      } = await getGroupPermissionsData({
        variables: { groupUuid: groupUuid },
      });

      if (
        !groupPermissionDataLoading &&
        groupPermissionData &&
        groupPermissionData.getAllGroupPermissions
      ) {
        setModulesPermissions(
          transformPermissions(groupPermissionData.getAllGroupPermissions),
        );
        setCommonHeaders(
          extractCommonHeaders(groupPermissionData.getAllGroupPermissions),
        );
      }
      if (
        groupPermissionDataError &&
        groupPermissionDataError.graphQLErrors &&
        groupPermissionDataError.graphQLErrors[0].message === "Signature has expired"
      ) {
        navigate(`${project_url}/signin`);
      }
    }
  };

  useEffect(() => {
    (() => getGroupPermissions())();
  }, []);

  const maxAdditional = Math.max(
    ...modulesPermissions.flatMap(mod => mod.modulepage.map(p => p.additional.length)),
  );

  const flatRows = modulesPermissions.flatMap(mod =>
    mod.modulepage.map((p, index) => ({
      moduleId: mod.moduleId,
      moduleName: mod.name,
      page: p,
      pageCount: mod.modulepage.length,
      isFirstPage: index === 0,
    })),
  );

  const handleToggle = (moduleId, pageId, section, permName) => {
    setModulesPermissions(prev =>
      prev.map(mod => {
        if (mod.moduleId !== moduleId) return mod;
        return {
          ...mod,
          modulepage: mod.modulepage.map(pg => {
            if (pg.modulepageId !== pageId) return pg;
            // Toggle access

            if (section === "access") {
              //if modulepage is "Configure Tenant" and check access
              //Edit should be checked
              var commonUpdated = [];

              if (
                mod.name == "General Settings" &&
                !pg.access == true &&
                (pg.name == "Configure Tenant" || pg.name == "Configure Terms")
              ) {
                commonUpdated = pg["common"].map(perm =>
                  perm.permission === "Edit"
                    ? (perm = { ...perm, checked: true })
                    : (perm = { ...perm, checked: false }),
                );
              } else if (
                mod.name == "Sprints" &&
                pg.name == "List" &&
                !pg.access == true
              ) {
                commonUpdated = pg["common"].map(perm =>
                  perm.permission === "View"
                    ? (perm = { ...perm, checked: true })
                    : (perm = { ...perm, checked: false }),
                );
              } else if (
                mod.name == "Reports" &&
                pg.name == "Download Applicants" &&
                !pg.access == true
              ) {
                commonUpdated = pg["common"].map(perm =>
                  perm.permission === "View"
                    ? (perm = { ...perm, checked: true })
                    : (perm = { ...perm, checked: false }),
                );
              } else {
                //uncheck all checbox when access changes
                commonUpdated = pg["common"].map(
                  perm => (perm = { ...perm, checked: false }),
                );
              }

              const additionalUpdated = pg["additional"].map(
                perm => (perm = { ...perm, checked: false }),
              );

              return {
                ...pg,
                access: !pg.access,
                common: commonUpdated,
                additional: additionalUpdated,
              };
            }
            // Toggle common or additional permissions
            const updated = pg[section].map(perm =>
              perm.permission === permName ? { ...perm, checked: !perm.checked } : perm,
            );
            return { ...pg, [section]: updated };
          }),
        };
      }),
    );
  };

  const [updateGroupModulepageAction] = useMutation(UPDATE_GROUP_PERMISSIONS, {
    awaitRefetchQueries: true,
  });

  const handleSave = () => {
    //check if access select for any page,
    //at least one of the permissions (common or additional) should be checked
    for (var i in modulesPermissions) {
      var moduleName = modulesPermissions[i].name;
      for (var j in modulesPermissions[i].modulepage) {
        var pg = modulesPermissions[i].modulepage[j];

        if (pg.access == true) {
          var checked = false;
          for (var k in pg.common) {
            if (pg.common[k].checked == true) checked = true;
          }
          if (checked == false) {
            for (var f in pg.additional) {
              if (pg.additional[f].checked == true) checked = true;
            }
          }
          if (checked == false) {
            setAlertMsg({
              title: "Error",
              msg:
                "At least one of permissions should be checked for " +
                moduleName +
                " " +
                pg.name +
                ".",
            });
            console.log("aLERT", alertMsg);
            return;
          }
        }
      }
    }

    modulesPermissions.map(mod => {
      mod.modulepage.map(pg => {
        const actionCommonPermission = pg.common
          .map(perm => `${perm.permission}:${perm.checked ? 1 : 0}`)
          .join("|");

        const actionAdditionalPermission = pg.additional
          .map(perm => `${perm.permission}:${perm.checked ? 1 : 0}`)
          .join("|");

        const { data, error } = updateGroupModulepageAction({
          variables: {
            groupUuid: groupUuid,
            modulepageId: parseInt(pg.modulepageId),
            actionCommonPermission: actionCommonPermission,
            actionAdditionalPermission: actionAdditionalPermission,
          },
        });
        if (error) {
          console.log("Error", error, data);
          setAlertMsg({
            title: "Error",
            msg: "Unfortunately an error occurred.",
          });
        } else if (data && data.updateGroupModulepageAction) {
          console.log("Success", data);
        }
      });
    });
    navigate(`${project_url}/groups`);
  };

  const cellStyle = { borderRight: "1px solid #ccc" };
  const cellStyleAdditional = {
    borderRight: "1px solid #ccc",
    textAlign: "left",
  };

  if (loadContent)
    if (permissionAccess)
      return (
        <div className={classes.root}>
          <Header
            breadcrumbs={[
              { label: groupName, link: null },
              { label: "Permissions", link: null },
            ]}
          />
          <div className={classes.container}>
            <Box mt={2} mb={2} textAlign="center">
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                Group Permissions
              </Typography>
            </Box>
            <Divider />
            <Box pb={4} />

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: "1px solid #e0e0e0", boxShadow: "none" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={cellStyle} align="center">
                      <strong>Module</strong>
                    </TableCell>
                    <TableCell rowSpan={2} sx={cellStyle} align="center">
                      <strong>Page</strong>
                    </TableCell>
                    <TableCell
                      colSpan={commonHeaders.length}
                      align="center"
                      sx={cellStyle}
                    >
                      <strong>Common Permissions</strong>
                    </TableCell>
                    <TableCell rowSpan={2} align="center">
                      <strong>Additional Permissions</strong>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {commonHeaders.map(perm => (
                      <TableCell key={perm} align="center" sx={cellStyle}>
                        <strong>{perm}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {flatRows.map(row => (
                    <TableRow key={`${row.moduleId}-${row.page.modulepageId}`}>
                      {row.isFirstPage && (
                        <TableCell rowSpan={row.pageCount} sx={cellStyle}>
                          {row.moduleName}
                        </TableCell>
                      )}
                      <TableCell sx={cellStyle}>
                        <Box
                          style={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "center",
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={row.page.access}
                            onChange={() =>
                              handleToggle(row.moduleId, row.page.modulepageId, "access")
                            }
                          />
                          {row.page.permission}
                        </Box>
                      </TableCell>

                      {commonHeaders.map(perm => (
                        <TableCell key={perm} align="center" sx={cellStyle}>
                          <Checkbox
                            size="small"
                            disabled={
                              (row.moduleName === "General Settings" &&
                                row.page.permission === "Configure Tenant") ||
                              (row.moduleName === "Sprints" &&
                                row.page.permission === "List") ||
                              (row.moduleName === "Reports" &&
                                row.page.permission === "Download Applicants") ||
                              (row.moduleName === "General Settings" &&
                                row.page.permission === "Configure Terms")
                                ? true
                                : row.page.access
                                  ? false
                                  : true
                            }
                            checked={
                              row.page.common.find(c => c.permission === perm)?.checked ||
                              false
                            }
                            onChange={() =>
                              handleToggle(
                                row.moduleId,
                                row.page.modulepageId,
                                "common",
                                perm,
                              )
                            }
                          />
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        {row.page.additional.length === 0 ? (
                          <>None</>
                        ) : (
                          row.page.additional.map(perm => (
                            <Box
                              key={perm.permission}
                              style={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                              }}
                            >
                              <Checkbox
                                size="small"
                                disabled={row.page.access ? false : true}
                                checked={perm.checked}
                                onChange={() =>
                                  handleToggle(
                                    row.moduleId,
                                    row.page.modulepageId,
                                    "additional",
                                    perm.permission,
                                  )
                                }
                              />
                              {perm.permission}
                            </Box>
                          ))
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box mt={4} textAlign="center">
              <Button
                variant="contained"
                className={classes.buttonStyle}
                onClick={handleSave}
              >
                Save
              </Button>
              <Button
                variant="text"
                color="primary"
                className={classes.outlinedButtonStyle}
                onClick={() => navigate(`${project_url}/groups`)}
              >
                Cancel
              </Button>
            </Box>

            {alertMsg.msg && (
              <AlertDialog
                title={alertMsg.title}
                contentMsg={alertMsg.msg}
                closeDialog={() => {
                  setAlertMsg([]);
                  alertMsg.title === "Success" && navigate(`${project_url}/groups`);
                }}
              />
            )}
          </div>
        </div>
      );
    else
      return (
        <div className={classes.root}>
          <div className={classes.container}>
            <AlertDialog
              title="No Permission"
              contentMsg='You do not have permission to access this page.
                            Click the "Done" button and navigate the system through the designated menu options on the sidebar menu.'
              closeDialog={() => {
                navigate(`${project_url}/tenant/dashboard`);
              }}
            />
          </div>
        </div>
      );
  else return;
};

export default GroupPermission;
